/**
 * Relatórios › Financeiro › Fornecedores.
 *
 * Duas seções: os pagamentos do MÊS (parcelas dos pedidos de compra × conta
 * corrente, por fornecedor, com a conciliação por pedido) e a PREVISÃO dos
 * próximos meses (matriz fornecedor × mês, com drill-down).
 *
 * A conciliação vem pronta do backend (flags por parcela/lançamento); a tela
 * mostra e oferece o "Vincular" para os pagamentos sem pedido/fornecedor.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import clsx from 'clsx';
import { Calendar, ChevronDown, ChevronRight, Link2, Truck } from 'lucide-react';
import { usePermissions } from '@components/PermissionGuard';
import { useUserPreferences, PREF_FIN_REPORT_SUPPLIERS } from '@/hooks/useUserPreferences';
import { financialReportsService } from '@/services/financialReportsService';
import { financialService } from '@/services/financialService';
import { providerService } from '@/services/providerService';
import { purchaseOrderService } from '@/services/purchaseOrderService';
import type { Provider } from '@/types/provider';
import type { PurchaseOrder } from '@/types/purchaseOrder';
import { PURCHASE_ORDER_STATUS_OPTIONS } from '@/types/purchaseOrder';
import type {
  SupplierAgendaItem,
  SupplierAgendaSituation,
  SupplierForecast,
  SupplierMatchRule,
  SupplierMonthReport,
  SupplierReportDocument,
  SupplierReportFlag,
  SupplierReportInstallment,
  SupplierReportRow,
  SupplierReportTransaction,
} from '@/types/financialReports';
import { formatCurrencyBRL, formatDateBR } from '@pages/financial/financialUtils';
import {
  ErrorBox,
  LoadingSpinner,
  ModalShell,
  SummaryCard,
  labelCls,
  primaryBtnCls,
  secondaryBtnCls,
  selectCls,
} from '@pages/financial/financialShared';
import { CompanySelect, getMonthOptions, getYearOptions, useCompanies } from './financialReportsShared';
import { competenceLabelPtBR } from './financialReportsUtils';
import { SupplierForecastMatrix } from './SupplierForecastMatrix';
import {
  AGENDA_SITUATION_META,
  FLAG_STYLES,
  MATCH_RULE_LABELS,
  agendaTotals,
  bookedTotal,
  documentKey,
  dueDateShiftLabel,
  groupAgendaByDay,
  primaryFlag,
  sortedFlags,
  supplierKey,
  weekdayShortBR,
} from './supplierReport';

interface SuppliersPrefs extends Record<string, unknown> {
  company_id?: number;
  months?: number;
}

const HORIZON_OPTIONS = [6, 12, 24];

// ─── Badges ─────────────────────────────────────────────────────

const FlagBadge: React.FC<{ flag: SupplierReportFlag }> = ({ flag }) => (
  <span
    className={clsx('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap', FLAG_STYLES[flag].cls)}
    title={FLAG_STYLES[flag].title}
  >
    {FLAG_STYLES[flag].label}
  </span>
);

const FlagList: React.FC<{ flags: SupplierReportFlag[] }> = ({ flags }) => {
  const list = sortedFlags(flags);
  if (list.length === 0) return null;
  return (
    <span className="inline-flex flex-wrap gap-1">
      {list.map(f => (
        <FlagBadge key={f} flag={f} />
      ))}
    </span>
  );
};

const OkBadge: React.FC = () => (
  <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-700" title="Parcelas e lançamentos conferem">
    ok
  </span>
);

const PaidBadge: React.FC<{ realized: boolean }> = ({ realized }) => (
  <span
    className={clsx(
      'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap',
      realized ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600',
    )}
  >
    {realized ? 'pago' : 'a pagar'}
  </span>
);

const poStatusLabel = (status: number): string =>
  PURCHASE_ORDER_STATUS_OPTIONS.find(o => o.value === status)?.label ?? String(status);

const DocumentLink: React.FC<{ doc: Pick<SupplierReportDocument, 'source' | 'documentId' | 'label'> }> = ({ doc }) =>
  doc.source === 'pc' ? (
    <RouterLink to={`/purchase-orders/${doc.documentId}`} className="text-emerald-600 font-medium hover:underline">
      {doc.label}
    </RouterLink>
  ) : (
    <RouterLink to={`/material-receipts/${doc.documentId}`} className="text-emerald-600 font-medium hover:underline">
      {doc.label}
    </RouterLink>
  );

// ─── Modal "Vincular" ───────────────────────────────────────────

interface LinkTarget {
  transaction: SupplierReportTransaction;
  providerId: number | null;
  purchaseOrderId: number | null;
}

const SupplierLinkModal: React.FC<{
  target: LinkTarget;
  providers: Provider[];
  onClose: () => void;
  onSaved: () => void;
}> = ({ target, providers, onClose, onSaved }) => {
  const [providerId, setProviderId] = useState<number | null>(target.providerId);
  const [purchaseOrderId, setPurchaseOrderId] = useState<number | null>(target.purchaseOrderId);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pedidos do fornecedor na empresa do lançamento — a lista do "Vincular".
  useEffect(() => {
    let active = true;
    if (providerId == null) {
      setOrders([]);
      return;
    }
    setOrdersLoading(true);
    purchaseOrderService
      .getPurchaseOrders({
        page: 1,
        pageSize: 200,
        provider_id: providerId,
        company_id: target.transaction.companyId,
        is_active: true,
        orderBy: 'order_date',
        orderDirection: 'desc',
      })
      .then(res => {
        if (!active) return;
        setOrders(res.data.filter(o => o.status !== 2));
      })
      .catch(() => {
        if (active) setOrders([]);
      })
      .finally(() => {
        if (active) setOrdersLoading(false);
      });
    return () => {
      active = false;
    };
  }, [providerId, target.transaction.companyId]);

  const handleSave = async () => {
    setError(null);
    if (providerId == null && purchaseOrderId == null) {
      setError('Informe o fornecedor');
      return;
    }
    try {
      setSaving(true);
      await financialService.linkTransactionSupplier(target.transaction.transactionId, { providerId, purchaseOrderId });
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao vincular lançamento');
    } finally {
      setSaving(false);
    }
  };

  const t = target.transaction;
  return (
    <ModalShell
      title="Vincular lançamento"
      icon={<Link2 size={20} className="text-emerald-600" />}
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} disabled={saving} className={secondaryBtnCls}>
            Cancelar
          </button>
          <button type="button" onClick={handleSave} disabled={saving} className={primaryBtnCls}>
            {saving ? 'Salvando...' : 'Vincular'}
          </button>
        </>
      }
    >
      <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm">
        <p className="font-medium text-slate-800 break-words">{t.description}</p>
        <p className="text-slate-500 text-xs mt-1">
          {formatDateBR(t.date)} · {formatCurrencyBRL(t.amount)}
          {t.accountName ? ` · ${t.accountName}` : ''}
        </p>
      </div>
      <div>
        <label className={labelCls}>Fornecedor *</label>
        <select
          className={selectCls}
          value={providerId ?? ''}
          onChange={e => {
            setProviderId(e.target.value === '' ? null : Number(e.target.value));
            setPurchaseOrderId(null);
          }}
        >
          <option value="">— Selecione —</option>
          {providers.map(p => (
            <option key={p.provider_id} value={p.provider_id}>
              {p.trade_name || p.legal_name}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={labelCls}>Pedido de compra</label>
        <select
          className={selectCls}
          value={purchaseOrderId ?? ''}
          onChange={e => setPurchaseOrderId(e.target.value === '' ? null : Number(e.target.value))}
          disabled={providerId == null || ordersLoading}
        >
          <option value="">— Só o fornecedor, sem pedido —</option>
          {orders.map(o => (
            <option key={o.purchase_order_id} value={o.purchase_order_id}>
              {o.order_number ? `PC ${o.order_number}` : `PC #${o.purchase_order_id}`}
              {o.order_date ? ` · ${formatDateBR(o.order_date)}` : ''}
              {o.total != null ? ` · ${formatCurrencyBRL(o.total)}` : ''}
              {` · ${poStatusLabel(o.status)}`}
            </option>
          ))}
        </select>
        <p className="text-xs text-slate-400 mt-1">
          {ordersLoading
            ? 'Carregando pedidos...'
            : providerId == null
              ? 'Escolha o fornecedor para listar os pedidos.'
              : `${orders.length} pedido(s) ativo(s) do fornecedor na empresa do lançamento.`}
        </p>
      </div>
      <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
        O lançamento passa a contar como pagamento do fornecedor (e do pedido, se informado) neste
        relatório. Editar o pedido depois não apaga nem altera este lançamento.
      </p>
      {error && <ErrorBox message={error} />}
    </ModalShell>
  );
};

// ─── Linhas da tabela do mês ─────────────────────────────────────

const MatchRuleBadge: React.FC<{ rule: SupplierMatchRule }> = ({ rule }) => {
  if (!rule) return null;
  const meta = MATCH_RULE_LABELS[rule];
  return (
    <span
      className={clsx(
        'inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap',
        rule === 'posicao' ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500',
      )}
      title={meta.title}
    >
      {meta.label}
    </span>
  );
};

const TransactionLine: React.FC<{
  t: SupplierReportTransaction;
  canLink: boolean;
  onLink: (t: SupplierReportTransaction) => void;
  linkLabel?: string;
  /** Mostra a parcela que o lançamento paga (detalhe do pedido). */
  showMatch?: boolean;
}> = ({ t, canLink, onLink, linkLabel = 'Vincular ao PC', showMatch = false }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 border-b border-slate-100 last:border-0 text-xs">
    <span className="text-slate-400 whitespace-nowrap w-20 shrink-0">{formatDateBR(t.date)}</span>
    <span className="text-slate-700 min-w-0 flex-1 break-words">
      <span className="text-slate-400">{t.cardInvoice ? 'cartão' : `#${t.transactionId}`}</span> {t.description}
      <span className="block text-[10px] text-slate-400">
        {[t.accountName, t.categoryLabel, t.updatedBy ? `por ${t.updatedBy}` : null].filter(Boolean).join(' · ')}
      </span>
      {showMatch && t.matchedInstallmentLabel && (
        <span className="block text-[10px] text-slate-500">
          paga a parcela {t.matchedInstallmentLabel} (vence {formatDateBR(t.matchedInstallmentDueDate ?? '')},{' '}
          {formatCurrencyBRL(t.matchedInstallmentValue ?? 0)})
        </span>
      )}
      {showMatch && !t.matchedInstallmentLabel && t.flags.includes('duplicado') && (
        <span className="block text-[10px] text-violet-700">não paga nenhuma parcela: todas já têm lançamento</span>
      )}
    </span>
    {showMatch && <MatchRuleBadge rule={t.matchRule} />}
    <PaidBadge realized={t.realized} />
    <FlagList flags={t.flags} />
    <span className="font-medium text-slate-800 whitespace-nowrap">{formatCurrencyBRL(t.amount)}</span>
    {canLink && !t.cardInvoice && (
      <button
        type="button"
        onClick={() => onLink(t)}
        className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700 hover:bg-emerald-100"
      >
        <Link2 size={12} />
        {linkLabel}
      </button>
    )}
  </div>
);

/**
 * Diagnóstico de uma parcela com valor divergente: parcela e lançamento lado a
 * lado, com tudo o que o analista precisa para decidir se é o mesmo pagamento
 * (e o valor mudou) ou se o pareamento juntou coisas diferentes.
 */
const DivergenceDiagnosis: React.FC<{ inst: SupplierReportInstallment }> = ({ inst }) => {
  const paid = inst.matchedAmount ?? 0;
  const pct = inst.value > 0 ? (inst.difference / inst.value) * 100 : 0;
  const rule = inst.matchRule ? MATCH_RULE_LABELS[inst.matchRule] : null;
  return (
    <div className="mt-1 mb-2 ml-8 rounded-lg border border-red-200 bg-red-50/60 p-3 text-xs">
      <p className="font-semibold text-red-700 mb-2">
        Valor divergente: pago {formatCurrencyBRL(paid)} para uma parcela de {formatCurrencyBRL(inst.value)} (
        {inst.difference > 0 ? '+' : ''}
        {formatCurrencyBRL(inst.difference)}, {pct > 0 ? '+' : ''}
        {pct.toFixed(1)}%)
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="rounded-md bg-white border border-slate-200 p-2">
          <p className="font-semibold text-slate-600 mb-1">Parcela do pedido {inst.label}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-slate-700">
            <dt className="text-slate-400">Vencimento</dt>
            <dd>
              {formatDateBR(inst.dueDate)}
              {inst.originalDueDate && (
                <span className="text-slate-500"> (no pedido: {formatDateBR(inst.originalDueDate)}, dia útil seguinte)</span>
              )}
            </dd>
            <dt className="text-slate-400">Valor</dt>
            <dd className="font-medium">{formatCurrencyBRL(inst.value)}</dd>
            <dt className="text-slate-400">Forma</dt>
            <dd>{inst.paymentMethod ?? '—'}</dd>
            <dt className="text-slate-400">Obs.</dt>
            <dd className="break-words">{inst.notes ?? '—'}</dd>
          </dl>
        </div>
        <div className="rounded-md bg-white border border-slate-200 p-2">
          <p className="font-semibold text-slate-600 mb-1">Lançamento #{inst.matchedTransactionId}</p>
          <dl className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5 text-slate-700">
            <dt className="text-slate-400">Data</dt>
            <dd>
              {formatDateBR(inst.matchedDate ?? '')} <PaidBadge realized={inst.realized} />
            </dd>
            <dt className="text-slate-400">Valor</dt>
            <dd className="font-medium">{formatCurrencyBRL(paid)}</dd>
            <dt className="text-slate-400">Descrição</dt>
            <dd className="break-words">{inst.matchedDescription ?? '—'}</dd>
            <dt className="text-slate-400">Conta</dt>
            <dd>{inst.matchedAccountName ?? '—'}</dd>
            <dt className="text-slate-400">Categoria</dt>
            <dd>{inst.matchedCategoryLabel ?? '—'}</dd>
            <dt className="text-slate-400">Lançado por</dt>
            <dd className="break-all">{inst.matchedUpdatedBy ?? '—'}</dd>
          </dl>
        </div>
      </div>
      {rule && (
        <p className="mt-2 text-slate-600">
          <span className="font-medium">Como foi pareado:</span> {rule.label} — {rule.title}
        </p>
      )}
      <p className="mt-1 text-slate-500">
        Se é o mesmo pagamento com valor ajustado, nada a fazer: o relatório passa a usar o valor do lançamento. Se for
        outro pagamento, use &quot;Alterar vínculo&quot; no lançamento ao lado para apontá-lo ao pedido certo (ou só ao
        fornecedor) e vincule o lançamento certo pelo botão Vincular.
      </p>
    </div>
  );
};

const DocumentDetail: React.FC<{
  doc: SupplierReportDocument;
  canLink: boolean;
  onLink: (t: SupplierReportTransaction) => void;
}> = ({ doc, canLink, onLink }) => (
  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 py-2">
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1">Parcelas do pedido no mês</p>
      {doc.installments.length === 0 ? (
        <p className="text-xs text-slate-400">Nenhuma parcela vence neste mês.</p>
      ) : (
        doc.installments.map(inst => (
          <React.Fragment key={inst.installmentId}>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 border-b border-slate-100 last:border-0 text-xs">
              <span className="w-8 shrink-0 font-medium text-slate-600">{inst.label}</span>
              <span
                className="text-slate-400 whitespace-nowrap"
                title={dueDateShiftLabel(inst.originalDueDate, inst.dueDate, inst.dueDateShift, formatDateBR) || undefined}
              >
                vence {formatDateBR(inst.dueDate)}
                {inst.originalDueDate && <span className="text-amber-600"> (pedido: {formatDateBR(inst.originalDueDate)})</span>}
              </span>
              {inst.paymentMethod && <span className="text-slate-400">{inst.paymentMethod}</span>}
              {inst.matchedTransactionId != null ? (
                <span className="text-slate-500">
                  → lançamento #{inst.matchedTransactionId} de {formatDateBR(inst.matchedDate ?? '')},{' '}
                  {formatCurrencyBRL(inst.matchedAmount ?? 0)}
                  {inst.difference !== 0 && (
                    <span className={clsx('ml-1 font-medium', inst.difference < 0 ? 'text-amber-700' : 'text-red-600')}>
                      ({inst.difference > 0 ? '+' : ''}
                      {formatCurrencyBRL(inst.difference)})
                    </span>
                  )}
                </span>
              ) : null}
              {inst.matchedTransactionId != null && <PaidBadge realized={inst.realized} />}
              <MatchRuleBadge rule={inst.matchRule} />
              <FlagList flags={inst.flags} />
              <span className="ml-auto font-medium text-slate-800 whitespace-nowrap">{formatCurrencyBRL(inst.value)}</span>
            </div>
            {inst.flags.includes('valor_divergente') && <DivergenceDiagnosis inst={inst} />}
          </React.Fragment>
        ))
      )}
    </div>
    <div>
      <p className="text-xs font-semibold text-slate-500 mb-1">Lançamentos no Conta Corrente no mês</p>
      {doc.transactions.length === 0 ? (
        <p className="text-xs text-slate-400">Nenhum lançamento neste mês.</p>
      ) : (
        doc.transactions.map(t => (
          <TransactionLine
            key={t.transactionId}
            t={t}
            // Provisão gerada pelo pedido não se revincula (o backend recusa): edita-se o pedido.
            canLink={canLink && t.updatedBy !== 'system:provision'}
            onLink={onLink}
            linkLabel="Alterar vínculo"
            showMatch
          />
        ))
      )}
    </div>
  </div>
);

// ─── Agenda de vencimentos ───────────────────────────────────────

type AgendaFilter = 'todas' | SupplierAgendaSituation;

const AGENDA_FILTERS: { key: AgendaFilter; label: string }[] = [
  { key: 'todas', label: 'Todas' },
  { key: 'a_pagar', label: 'A pagar' },
  { key: 'vencida', label: 'Vencidas' },
  { key: 'pago', label: 'Pagas' },
];

const AgendaSituationBadge: React.FC<{ situation: SupplierAgendaSituation }> = ({ situation }) => (
  <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium whitespace-nowrap', AGENDA_SITUATION_META[situation].cls)}>
    {AGENDA_SITUATION_META[situation].label}
  </span>
);

/**
 * "O que vence em cada dia": uma linha por parcela do mês, agrupada pelo dia
 * ÚTIL de vencimento. Parcela combinada para sábado, domingo ou feriado
 * aparece no próximo dia útil, com a data do pedido ao lado.
 */
const AgendaSection: React.FC<{ agenda: SupplierAgendaItem[]; periodLabel: string }> = ({ agenda, periodLabel }) => {
  const [filter, setFilter] = useState<AgendaFilter>('todas');
  const totals = useMemo(() => agendaTotals(agenda), [agenda]);
  const days = useMemo(
    () => groupAgendaByDay(filter === 'todas' ? agenda : agenda.filter(a => a.situation === filter)),
    [agenda, filter],
  );
  const countOf = (key: AgendaFilter): number =>
    key === 'todas' ? agenda.length : agenda.filter(a => a.situation === key).length;

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Calendar size={18} className="text-emerald-600" />
        <h2 className="text-lg font-semibold text-slate-800">Agenda de vencimentos de {periodLabel}</h2>
        <span className="text-xs text-slate-400">
          parcelas dos pedidos por dia útil de vencimento · sábado, domingo e feriado caem no dia útil seguinte
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {AGENDA_FILTERS.map(f => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            className={clsx(
              'rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              filter === f.key
                ? 'bg-emerald-600 border-emerald-600 text-white'
                : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50',
            )}
          >
            {f.label} ({countOf(f.key)})
          </button>
        ))}
        <span className="ml-auto text-xs text-slate-500">
          {formatCurrencyBRL(totals.total)} no mês · <span className="text-emerald-700">{formatCurrencyBRL(totals.paid)} pago</span> ·{' '}
          {formatCurrencyBRL(totals.open)} a pagar
          {totals.overdue > 0 && <span className="text-red-600"> · {formatCurrencyBRL(totals.overdue)} vencido</span>}
        </span>
      </div>
      {days.length === 0 ? (
        <p className="text-sm text-slate-500 py-6 text-center">Nenhuma parcela {filter === 'todas' ? 'vence' : 'nesta situação'} em {periodLabel}.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-slate-600">
                <th className="text-left py-2 px-3 font-semibold whitespace-nowrap">Vencimento</th>
                <th className="text-left py-2 px-3 font-semibold">Fornecedor</th>
                <th className="text-left py-2 px-3 font-semibold whitespace-nowrap">Documento</th>
                <th className="text-left py-2 px-3 font-semibold whitespace-nowrap">Parcela</th>
                <th className="text-left py-2 px-3 font-semibold">Forma</th>
                <th className="text-left py-2 px-3 font-semibold">Situação</th>
                <th className="text-left py-2 px-3 font-semibold whitespace-nowrap">Lançamento</th>
                <th className="text-right py-2 px-3 font-semibold whitespace-nowrap">Valor</th>
              </tr>
            </thead>
            <tbody>
              {days.map(day => (
                <React.Fragment key={day.date}>
                  <tr className="bg-slate-50 border-t border-slate-200">
                    <td colSpan={7} className="py-1.5 px-3 text-xs font-semibold text-slate-700">
                      {weekdayShortBR(day.date)} {formatDateBR(day.date)}
                      <span className="ml-2 font-normal text-slate-500">
                        {day.items.length} parcela{day.items.length !== 1 ? 's' : ''}
                        {day.paid > 0 && <span className="text-emerald-700"> · {formatCurrencyBRL(day.paid)} pago</span>}
                        {day.open > 0 && <span> · {formatCurrencyBRL(day.open)} a pagar</span>}
                      </span>
                    </td>
                    <td className="py-1.5 px-3 text-right text-xs font-semibold text-slate-700 whitespace-nowrap">
                      {formatCurrencyBRL(day.total)}
                    </td>
                  </tr>
                  {day.items.map(item => (
                    <tr key={`${item.source}-${item.installmentId}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2 px-3 whitespace-nowrap text-slate-700">
                        {formatDateBR(item.dueDate)}
                        {item.originalDueDate && (
                          <span
                            className="block text-[11px] text-amber-600"
                            title={dueDateShiftLabel(item.originalDueDate, item.dueDate, item.dueDateShift, formatDateBR)}
                          >
                            pedido: {weekdayShortBR(item.originalDueDate)} {formatDateBR(item.originalDueDate)}
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-slate-800">{item.providerName}</td>
                      <td className="py-2 px-3 whitespace-nowrap">
                        <DocumentLink doc={{ source: item.source, documentId: item.documentId, label: item.documentLabel }} />
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-slate-600">
                        {item.label}
                        {item.notes && <span className="block text-[11px] text-slate-400 truncate max-w-[180px]" title={item.notes}>{item.notes}</span>}
                      </td>
                      <td className="py-2 px-3 text-slate-600">{item.paymentMethod ?? '—'}</td>
                      <td className="py-2 px-3">
                        <span className="inline-flex flex-wrap items-center gap-1">
                          <AgendaSituationBadge situation={item.situation} />
                          <FlagList flags={item.flags.filter(f => f !== 'vencida' && f !== 'sem_lancamento')} />
                        </span>
                      </td>
                      <td className="py-2 px-3 whitespace-nowrap text-xs text-slate-500">
                        {item.matchedTransactionId != null ? (
                          <>
                            #{item.matchedTransactionId} · {formatDateBR(item.matchedDate ?? '')}
                            {item.matchedAmount != null && item.matchedAmount !== item.value && (
                              <span className="text-amber-700"> · {formatCurrencyBRL(item.matchedAmount)}</span>
                            )}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-2 px-3 text-right whitespace-nowrap font-medium text-slate-800">{formatCurrencyBRL(item.value)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── View ────────────────────────────────────────────────────────

export const SuppliersReportView: React.FC = () => {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [horizon, setHorizon] = useState(6);

  const [report, setReport] = useState<SupplierMonthReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [forecast, setForecast] = useState<SupplierForecast | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [forecastError, setForecastError] = useState<string | null>(null);

  const [expandedSuppliers, setExpandedSuppliers] = useState<Set<string>>(new Set());
  const [expandedDocs, setExpandedDocs] = useState<Set<string>>(new Set());
  const [linkTarget, setLinkTarget] = useState<LinkTarget | null>(null);
  const [providers, setProviders] = useState<Provider[]>([]);

  const { companies, companiesError } = useCompanies();
  const { checkPermission } = usePermissions();
  const canLink = checkPermission('financial.transactions.write').hasPermission;

  const { prefs, loaded: prefsLoaded, save: savePrefs } = useUserPreferences<SuppliersPrefs>(PREF_FIN_REPORT_SUPPLIERS);
  const [filtersReady, setFiltersReady] = useState(false);

  useEffect(() => {
    if (!prefsLoaded) return;
    if (prefs?.company_id != null) setCompanyId(prefs.company_id);
    if (prefs?.months != null && HORIZON_OPTIONS.includes(prefs.months)) setHorizon(prefs.months);
    setFiltersReady(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefsLoaded]);

  useEffect(() => {
    if (!filtersReady) return;
    savePrefs({ company_id: companyId ?? undefined, months: horizon });
  }, [filtersReady, companyId, horizon, savePrefs]);

  // Fornecedores para o "Vincular" — só quem pode vincular precisa da lista.
  useEffect(() => {
    if (!canLink) return;
    let active = true;
    providerService
      .getProviders({ page: 1, pageSize: 500 })
      .then(res => {
        if (active) setProviders(res.data.filter(p => p.is_active));
      })
      .catch(() => {
        if (active) setProviders([]);
      });
    return () => {
      active = false;
    };
  }, [canLink]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await financialReportsService.getSuppliersReport(year, month, companyId);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório de fornecedores');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [year, month, companyId]);

  const fetchForecast = useCallback(async () => {
    setForecastLoading(true);
    setForecastError(null);
    try {
      const data = await financialReportsService.getSuppliersForecast(horizon, companyId);
      setForecast(data);
    } catch (err) {
      setForecastError(err instanceof Error ? err.message : 'Erro ao carregar previsão de fornecedores');
      setForecast(null);
    } finally {
      setForecastLoading(false);
    }
  }, [horizon, companyId]);

  useEffect(() => {
    if (filtersReady) fetchReport();
  }, [fetchReport, filtersReady]);

  useEffect(() => {
    if (filtersReady) fetchForecast();
  }, [fetchForecast, filtersReady]);

  const refreshAll = () => {
    fetchReport();
    fetchForecast();
  };

  const toggle = (setter: React.Dispatch<React.SetStateAction<Set<string>>>, key: string) =>
    setter(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const openLink = (t: SupplierReportTransaction) =>
    setLinkTarget({
      transaction: t,
      providerId: t.providerId,
      purchaseOrderId: t.suggestedPurchaseOrderId ?? t.purchaseOrderId ?? null,
    });

  const periodLabel = useMemo(() => competenceLabelPtBR(year, month), [year, month]);

  const renderSupplierRow = (row: SupplierReportRow) => {
    const key = supplierKey(row.providerId);
    const open = expandedSuppliers.has(key);
    const main = primaryFlag(row.flags);
    return (
      <React.Fragment key={key}>
        <tr className="border-b border-slate-100 hover:bg-slate-50">
          <td className="py-2 px-3">
            <button
              type="button"
              onClick={() => {
                toggle(setExpandedSuppliers, key);
                // Ao abrir o fornecedor, os pedidos com pendência já vêm
                // abertos: é neles que o analista precisa olhar.
                if (!open) {
                  setExpandedDocs(prev => {
                    const next = new Set(prev);
                    row.documents.filter(d => d.issueCount > 0).forEach(d => next.add(documentKey(row.providerId, d)));
                    return next;
                  });
                }
              }}
              className="inline-flex items-center gap-1.5 text-left font-medium text-slate-800 hover:text-emerald-700"
              aria-expanded={open}
            >
              {open ? <ChevronDown size={14} className="shrink-0" /> : <ChevronRight size={14} className="shrink-0" />}
              <span>{row.providerName}</span>
            </button>
            <span className="block text-[10px] text-slate-400 pl-5">
              {row.documents.length} pedido(s)
              {row.unlinkedTransactions.length > 0 ? ` · ${row.unlinkedTransactions.length} sem pedido` : ''}
            </span>
          </td>
          <td className="py-2 px-3 text-right whitespace-nowrap text-slate-700">{formatCurrencyBRL(row.expected)}</td>
          <td className="py-2 px-3 text-right whitespace-nowrap text-slate-900 font-medium">{formatCurrencyBRL(bookedTotal(row))}</td>
          <td className="py-2 px-3 text-right whitespace-nowrap text-emerald-700">{formatCurrencyBRL(row.realized)}</td>
          <td className="py-2 px-3 text-right whitespace-nowrap text-slate-600">{formatCurrencyBRL(row.planned)}</td>
          <td
            className={clsx(
              'py-2 px-3 text-right whitespace-nowrap font-medium',
              row.difference === 0 ? 'text-slate-400' : row.difference > 0 ? 'text-red-600' : 'text-amber-700',
            )}
          >
            {row.difference === 0 ? '–' : `${row.difference > 0 ? '+' : ''}${formatCurrencyBRL(row.difference)}`}
          </td>
          <td className="py-2 px-3">
            {main ? (
              <span className="inline-flex items-center gap-1">
                <FlagBadge flag={main} />
                {row.issueCount > 1 && <span className="text-[10px] text-slate-400">+{row.issueCount - 1}</span>}
              </span>
            ) : (
              <OkBadge />
            )}
          </td>
        </tr>
        {open &&
          row.documents.map(doc => {
            const dkey = documentKey(row.providerId, doc);
            const dopen = expandedDocs.has(dkey);
            const dmain = primaryFlag(doc.flags);
            return (
              <React.Fragment key={dkey}>
                <tr className="border-b border-slate-100 bg-slate-50/60">
                  <td className="py-2 px-3 pl-6 sm:pl-10">
                    <button
                      type="button"
                      onClick={() => toggle(setExpandedDocs, dkey)}
                      className="inline-flex items-center gap-1.5 text-left text-slate-700 hover:text-emerald-700"
                      aria-expanded={dopen}
                    >
                      {dopen ? <ChevronDown size={12} className="shrink-0" /> : <ChevronRight size={12} className="shrink-0" />}
                      <DocumentLink doc={doc} />
                    </button>
                    <span className="block text-[10px] text-slate-400 pl-5">
                      {doc.source === 'pc' ? poStatusLabel(doc.status) : 'Recebimento'}
                      {doc.docTotal != null ? ` · total ${formatCurrencyBRL(doc.docTotal)}` : ''}
                      {` · parcelas ${formatCurrencyBRL(doc.installmentsTotal)}`}
                      {companyId == null && doc.companyId != null
                        ? ` · ${companies.find(c => c.company_id === doc.companyId)?.name ?? `Empresa ${doc.companyId}`}`
                        : ''}
                    </span>
                  </td>
                  <td className="py-2 px-3 text-right whitespace-nowrap text-slate-600">{formatCurrencyBRL(doc.expected)}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap text-slate-800">{formatCurrencyBRL(doc.realized + doc.planned)}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap text-emerald-700">{formatCurrencyBRL(doc.realized)}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap text-slate-600">{formatCurrencyBRL(doc.planned)}</td>
                  <td className="py-2 px-3 text-right whitespace-nowrap text-slate-400">
                    {doc.realized + doc.planned - doc.expected === 0
                      ? '–'
                      : formatCurrencyBRL(doc.realized + doc.planned - doc.expected)}
                  </td>
                  <td className="py-2 px-3">{dmain ? <FlagList flags={doc.flags} /> : <OkBadge />}</td>
                </tr>
                {dopen && (
                  <tr className="border-b border-slate-100 bg-white">
                    <td colSpan={7} className="px-3 sm:px-10 py-1">
                      <DocumentDetail doc={doc} canLink={canLink} onLink={openLink} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        {open && row.unlinkedTransactions.length > 0 && (
          <tr className="border-b border-slate-100 bg-sky-50/40">
            <td colSpan={7} className="px-3 sm:px-10 py-2">
              <p className="text-xs font-semibold text-sky-700 mb-1">
                Sem pedido vinculado
                <span className="font-normal text-slate-500"> — pagamentos ao fornecedor que não apontam para nenhum pedido de compra</span>
              </p>
              {row.unlinkedTransactions.map(t => (
                <TransactionLine key={t.transactionId} t={t} canLink={canLink && t.providerId != null} onLink={openLink} />
              ))}
            </td>
          </tr>
        )}
      </React.Fragment>
    );
  };

  return (
    <div className="space-y-6">
      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Mês</label>
            <select className={`${selectCls} w-full sm:w-32`} value={month} onChange={e => setMonth(Number(e.target.value))}>
              {getMonthOptions().map(o => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Ano</label>
            <select className={`${selectCls} w-full sm:w-28`} value={year} onChange={e => setYear(Number(e.target.value))}>
              {getYearOptions().map(y => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
          <div className="w-full sm:w-auto">
            <label className={labelCls}>Empresa</label>
            <CompanySelect companies={companies} value={companyId} onChange={setCompanyId} />
          </div>
          <button onClick={refreshAll} disabled={loading || forecastLoading} className={`${primaryBtnCls} w-full sm:w-auto justify-center`}>
            <Calendar size={16} />
            {loading ? 'Carregando...' : 'Atualizar'}
          </button>
        </div>
        {companiesError && <p className="text-xs text-red-600 mt-2">{companiesError}</p>}
      </div>

      {error && <ErrorBox message={error} onRetry={fetchReport} />}
      {loading && <LoadingSpinner />}

      {!loading && !error && report && (
        <>
          {/* Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <SummaryCard label="Previsto pelas parcelas" value={formatCurrencyBRL(report.totals.expected)} hint="parcelas de pedidos que vencem no mês" />
            <SummaryCard label="Lançado no mês" value={formatCurrencyBRL(report.totals.realized + report.totals.planned)} hint="conta corrente, com fornecedor" />
            <SummaryCard label="Pago até hoje" value={formatCurrencyBRL(report.totals.realized)} valueClassName="text-emerald-600" />
            <SummaryCard label="A pagar" value={formatCurrencyBRL(report.totals.planned)} valueClassName="text-slate-700" />
            <SummaryCard
              label="Discrepâncias"
              value={String(report.totals.issueCount)}
              valueClassName={report.totals.issueCount > 0 ? 'text-amber-700' : 'text-slate-900'}
              hint={
                report.totals.difference === 0
                  ? 'lançado = previsto'
                  : `lançado − previsto: ${report.totals.difference > 0 ? '+' : ''}${formatCurrencyBRL(report.totals.difference)}`
              }
            />
          </div>

          {/* Agenda de vencimentos por dia útil */}
          <AgendaSection agenda={report.agenda ?? []} periodLabel={periodLabel} />

          {/* Pagamentos do mês */}
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Truck size={18} className="text-emerald-600" />
              <h2 className="text-lg font-semibold text-slate-800">Pagamentos de {periodLabel}</h2>
              <span className="text-xs text-slate-400">
                parcelas dos pedidos de compra × lançamentos do Conta Corrente, por fornecedor
              </span>
            </div>
            {report.suppliers.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">Nenhuma parcela ou pagamento a fornecedor em {periodLabel}.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px] text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-slate-600">
                      <th className="text-left py-2 px-3 font-semibold">Fornecedor</th>
                      <th className="text-right py-2 px-3 font-semibold whitespace-nowrap" title="Parcelas dos pedidos que vencem no mês">
                        Previsto
                      </th>
                      <th className="text-right py-2 px-3 font-semibold whitespace-nowrap" title="Lançamentos do Conta Corrente no mês (pago + a pagar)">
                        Lançado
                      </th>
                      <th className="text-right py-2 px-3 font-semibold whitespace-nowrap">Pago</th>
                      <th className="text-right py-2 px-3 font-semibold whitespace-nowrap">A pagar</th>
                      <th className="text-right py-2 px-3 font-semibold whitespace-nowrap" title="Lançado − previsto">
                        Diferença
                      </th>
                      <th className="text-left py-2 px-3 font-semibold">Situação</th>
                    </tr>
                  </thead>
                  <tbody>{report.suppliers.map(renderSupplierRow)}</tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold border-t border-slate-200 text-slate-800">
                      <td className="py-2 px-3">Total</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">{formatCurrencyBRL(report.totals.expected)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">{formatCurrencyBRL(report.totals.realized + report.totals.planned)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap text-emerald-700">{formatCurrencyBRL(report.totals.realized)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">{formatCurrencyBRL(report.totals.planned)}</td>
                      <td className="py-2 px-3 text-right whitespace-nowrap">
                        {report.totals.difference === 0 ? '–' : formatCurrencyBRL(report.totals.difference)}
                      </td>
                      <td className="py-2 px-3" />
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>

          {/* Candidatos sem fornecedor */}
          {report.candidates.length > 0 && (
            <div className="bg-white border border-amber-200 rounded-xl shadow-sm p-4">
              <div className="flex flex-wrap items-center gap-2 mb-1">
                <Link2 size={18} className="text-amber-600" />
                <h2 className="text-lg font-semibold text-slate-800">Lançamentos sem fornecedor que citam um pedido</h2>
              </div>
              <p className="text-xs text-slate-500 mb-3">
                Saídas de {periodLabel} sem fornecedor cuja descrição menciona &quot;Pedido N&quot; de um pedido de compra
                conhecido. Não entram nos totais acima até serem vinculadas.
              </p>
              {report.candidates.map(t => (
                <TransactionLine key={t.transactionId} t={t} canLink={canLink} onLink={openLink} linkLabel="Vincular" />
              ))}
            </div>
          )}
        </>
      )}

      {/* Previsão */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <Calendar size={18} className="text-emerald-600" />
          <h2 className="text-lg font-semibold text-slate-800">Previsão dos próximos meses</h2>
          <span className="text-xs text-slate-400">
            do mês corrente em diante · parcela paga entra pela data do lançamento; sem lançamento, pelo vencimento do pedido em dia útil
          </span>
          <div className="ml-auto flex items-center gap-2">
            <label className="text-xs text-slate-500">Horizonte</label>
            <select className={`${selectCls} w-28`} value={horizon} onChange={e => setHorizon(Number(e.target.value))}>
              {HORIZON_OPTIONS.map(m => (
                <option key={m} value={m}>
                  {m} meses
                </option>
              ))}
            </select>
          </div>
        </div>
        {forecastError && <ErrorBox message={forecastError} onRetry={fetchForecast} />}
        {forecastLoading && <LoadingSpinner />}
        {!forecastLoading && !forecastError && forecast && <SupplierForecastMatrix data={forecast} />}
      </div>

      {linkTarget && (
        <SupplierLinkModal
          target={linkTarget}
          providers={providers}
          onClose={() => setLinkTarget(null)}
          onSaved={refreshAll}
        />
      )}
    </div>
  );
};

export default SuppliersReportView;
